import React, { useState } from 'react';
import { Search, Book } from 'lucide-react';

interface APIItem {
  name: string;
  description: string;
  category: 'react' | 'vue' | 'core' | 'helpers' | 'types';
  signature: string;
  example: string;
  params?: Array<{
    name: string;
    type: string;
    description: string;
    optional?: boolean;
  }>;
  returns?: {
    type: string;
    description: string;
  };
}

const ApiReferencePage: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  const apiItems: APIItem[] = [
    // ===== REACT HOOKS =====
    {
      name: 'useAddressForm',
      category: 'react',
      description: 'Main React hook for managing address forms with validation, async data fetching, and country-specific field handling',
      signature: 'useAddressForm(options?: AddressClientOptions): UseAddressFormReturn',
      params: [
        {
          name: 'locale',
          type: "'en' | 'ar'",
          description: 'Locale for the form (English or Arabic)',
          optional: true,
        },
        {
          name: 'validateOnChange',
          type: 'boolean',
          description: 'Enable real-time validation as user types (with debounce)',
          optional: true,
        },
        {
          name: 'validateOnBlur',
          type: 'boolean',
          description: 'Enable validation when field loses focus',
          optional: true,
        },
        {
          name: 'validationDelay',
          type: 'number',
          description: 'Debounce delay in milliseconds for validateOnChange (default: 300)',
          optional: true,
        },
        {
          name: 'countryOptions',
          type: 'CountryOptionsInput',
          description: 'Country options config (static array or async with queryFn + transform)',
          optional: true,
        },
        {
          name: 'cityOptions',
          type: 'CityOptionsInput',
          description: 'City options config (static array or async with queryFn + transform)',
          optional: true,
        },
        {
          name: 'districtOptions',
          type: 'DistrictOptionsInput',
          description: 'District options config (static array or async with queryFn + transform)',
          optional: true,
        },
        {
          name: 'defaultValues',
          type: 'AddressFormData | Promise<AddressFormData> | (() => AddressFormData | Promise<AddressFormData>)',
          description: 'Default/initial values (sync, async, or function)',
          optional: true,
        },
        {
          name: 'enableNationalAddress',
          type: 'boolean',
          description: 'Enable Saudi National Address fields (default: true for SA)',
          optional: true,
        },
        {
          name: 'requiredFields',
          type: '{ postal_code?: boolean; description?: boolean; building_number?: boolean }',
          description: 'Configure which fields are required',
          optional: true,
        },
      ],
      returns: {
        type: 'UseAddressFormReturn',
        description: 'Complete form state, methods, and async data',
      },
      example: `import { useAddressForm } from 'salla-address-manager/react';

function AddressForm() {
  const form = useAddressForm({
    locale: 'en',
    validateOnChange: true,
    validateOnBlur: true,
    validationDelay: 300,
    // Async API integration
    countryOptions: {
      queryFn: () => fetch('/api/countries').then(r => r.json()),
      transform: (res) => res.data.data
    },
    cityOptions: {
      queryFn: (countryId) => 
        fetch(\`/api/countries/\${countryId}/cities\`).then(r => r.json()),
      transform: (res) => res.data.data
    },
    defaultValues: {
      country: '1473353380', // Saudi Arabia
      city: 'riyadh'
    }
  });

  const handleSubmit = form.handleSubmit(
    async (data) => {
      // Valid data
      await fetch('/api/addresses', {
        method: 'POST',
        body: JSON.stringify(data)
      });
    },
    (errors) => {
      // Validation errors
      console.error(errors);
    }
  );

  return (
    <form onSubmit={handleSubmit}>
      <select {...form.getFieldProps('country')}>
        {form.countries.map(c => (
          <option key={c.id} value={c.id}>{c.name}</option>
        ))}
      </select>
      
      <select {...form.getFieldProps('city')} disabled={!form.data.country}>
        {form.currentCities.map(c => (
          <option key={c.id} value={c.id}>{c.name}</option>
        ))}
      </select>
      
      <input {...form.getFieldProps('street')} />
      {form.errors.street && <span>{form.errors.street}</span>}
      
      <button type="submit" disabled={!form.state.isValid}>
        {form.state.isSubmitting ? 'Saving...' : 'Save'}
      </button>
    </form>
  );
}`,
    },
    {
      name: 'getFieldProps',
      category: 'react',
      description: 'Get all necessary props for a form field including value, onChange, onBlur, error, options (for selects), and ARIA attributes',
      signature: "getFieldProps(fieldName: string, customProps?: Record<string, unknown>): FieldProps | null",
      params: [
        {
          name: 'fieldName',
          type: 'string',
          description: 'Name of the field (country, city, district, street, etc.)',
        },
        {
          name: 'customProps',
          type: 'Record<string, unknown>',
          description: 'Optional custom props to override defaults',
          optional: true,
        },
      ],
      returns: {
        type: 'FieldProps',
        description: 'Complete props object ready to spread onto input/select elements',
      },
      example: `const form = useAddressForm({ locale: 'en' });

// For input fields
<input {...form.getFieldProps('street')} />

// For select fields (automatically includes options)
<select {...form.getFieldProps('country')}>
  {form.getFieldProps('country').options.map(opt => (
    <option key={opt.value} value={opt.value}>{opt.label}</option>
  ))}
</select>

// With custom overrides
<input {...form.getFieldProps('street', { className: 'custom-class' })} />

// Includes ARIA attributes automatically:
// - aria-invalid (when field has error)
// - aria-describedby (for error message)
// - required (based on validation rules)
// - disabled (for dependent fields like city/district)`,
    },
    {
      name: 'useNationalAddress',
      category: 'react',
      description: 'React hook for Saudi National Address autocomplete with auto-population',
      signature: 'useNationalAddress(options: UseNationalAddressOptions): UseNationalAddressReturn',
      params: [
        {
          name: 'form',
          type: 'UseAddressFormReturn',
          description: 'The form instance from useAddressForm',
        },
        {
          name: 'queryFn',
          type: '({ address, customer_id }) => Promise<unknown>',
          description: 'Function to fetch national address autocomplete results',
        },
        {
          name: 'transform',
          type: '(response: unknown) => NationalAddressAutocompleteItem[]',
          description: 'Transform API response to autocomplete items',
          optional: true,
        },
        {
          name: 'debounceMs',
          type: 'number',
          description: 'Debounce delay for search (default: 400ms)',
          optional: true,
        },
        {
          name: 'customerId',
          type: 'number | string',
          description: 'Customer ID for national address lookup',
          optional: true,
        },
        {
          name: 'onSelect',
          type: '(formData: AddressFormData, item: NationalAddressAutocompleteItem) => void',
          description: 'Callback when address is selected',
          optional: true,
        },
      ],
      returns: {
        type: 'UseNationalAddressReturn',
        description: 'Autocomplete state, results, and methods',
      },
      example: `import { useAddressForm, useNationalAddress } from 'salla-address-manager/react';

function NationalAddressForm() {
  const form = useAddressForm({
    locale: 'ar',
    enableNationalAddress: true,
    defaultValues: { country: '1473353380' } // Saudi Arabia
  });

  const nationalAddress = useNationalAddress({
    form, // Auto-populate form on selection
    queryFn: ({ address, customer_id }) =>
      fetch('/api/national-address/search', {
        method: 'POST',
        body: JSON.stringify({ address, customer_id })
      }).then(r => r.json()),
    transform: (res) => res.data.data,
    debounceMs: 400,
    customerId: 123,
    onSelect: (formData, item) => {
      console.log('Auto-populated:', formData);
    }
  });

  return (
    <form onSubmit={form.handleSubmit}>
      <div>
        <input 
          {...nationalAddress.getInputProps()} 
          placeholder="AAAA1234"
          maxLength={8}
        />
        {nationalAddress.isLoading && <span>جاري البحث...</span>}
        
        {nationalAddress.results.length > 0 && (
          <div className="dropdown">
            {nationalAddress.results.map(item => (
              <button
                key={item.value}
                onClick={() => nationalAddress.selectResult(item)}
              >
                {item.label}
              </button>
            ))}
          </div>
        )}
      </div>
      
      {/* Auto-populated fields */}
      <input {...form.getFieldProps('building_number')} />
      <input {...form.getFieldProps('postal_code')} />
    </form>
  );
}`,
    },
    
    // ===== VUE 3 COMPOSABLES =====
    {
      name: 'useAddressForm (Vue 3)',
      category: 'vue',
      description: 'Vue 3 composable for managing address forms with validation, async data fetching, and country-specific fields',
      signature: 'useAddressForm(options?: UseAddressClientOptions): UseAddressFormReturn',
      params: [
        {
          name: 'locale',
          type: "'en' | 'ar'",
          description: 'Locale for the form (English or Arabic)',
          optional: true,
        },
        {
          name: 'validateOnChange',
          type: 'boolean',
          description: 'Enable real-time validation as user types',
          optional: true,
        },
        {
          name: 'validateOnBlur',
          type: 'boolean',
          description: 'Enable validation when field loses focus',
          optional: true,
        },
        {
          name: 'initialData',
          type: 'AddressFormData',
          description: 'Initial form data',
          optional: true,
        },
      ],
      returns: {
        type: 'Object',
        description: 'Form state (data, errors, fields), methods (setValue, handleSubmit, toAPIPayload, getFieldProps), and computed values (currentCities, currentDistricts, loading, meta)',
      },
      example: `<script setup lang="ts">
import { useAddressForm } from 'salla-address-manager/vue3';

const form = useAddressForm({
  locale: 'en',
  validateOnChange: true
});

// Use getFieldProps for automatic configuration
const countryProps = form.getFieldProps('country');

// Transform to API payload
const handleSubmit = form.handleSubmit(
  async (data) => {
    const payload = form.toAPIPayload({
      fieldMappings: {
        country: 'country_id',
        city: 'city_id'
      },
      includeNames: ['district'],
      defaults: { type: 'delivery' }
    });
    
    await fetch('/api/addresses', {
      method: 'POST',
      body: JSON.stringify(payload)
    });
  }
);
</script>

<template>
  <form @submit.prevent="handleSubmit">
    <!-- Using getFieldProps -->
    <select v-bind="form.getFieldProps('country')">
      <option v-for="c in form.countries.value" :key="c.id" :value="c.id">
        {{ c.name }}
      </option>
    </select>
    
    <!-- Using currentCities computed -->
    <select v-bind="form.getFieldProps('city')">
      <option v-for="city in form.currentCities.value" :key="city.id" :value="city.id">
        {{ city.name }}
      </option>
    </select>
    
    <button :disabled="!form.meta.value.isValid">
      Save
    </button>
  </form>
</template>`,
    },
    {
      name: 'useNationalAddress (Vue 3)',
      category: 'vue',
      description: 'Vue 3 composable for Saudi National Address autocomplete with auto-population',
      signature: 'useNationalAddress(options: UseNationalAddressOptions): UseNationalAddressReturn',
      params: [
        {
          name: 'form',
          type: 'UseAddressFormReturn',
          description: 'The form instance from useAddressForm',
        },
        {
          name: 'queryFn',
          type: '({ address, customer_id }) => Promise<unknown>',
          description: 'Function to fetch national address results',
        },
        {
          name: 'transform',
          type: '(response: unknown) => NationalAddressItem[]',
          description: 'Transform API response',
          optional: true,
        },
        {
          name: 'debounceMs',
          type: 'number',
          description: 'Debounce delay (default: 400ms)',
          optional: true,
        },
        {
          name: 'customerId',
          type: 'number',
          description: 'Customer ID for lookup',
          optional: true,
        },
      ],
      returns: {
        type: 'Object',
        description: 'Autocomplete state (searchValue, items, isLoading, error) and methods (handleSelect, clear, retry)',
      },
      example: `<script setup lang="ts">
import { useAddressForm, useNationalAddress } from 'salla-address-manager/vue3';

const form = useAddressForm({ locale: 'ar' });

const nationalAddress = useNationalAddress({
  form,
  queryFn: ({ address, customer_id }) =>
    fetch('/api/national-addresses').then(r => r.json()),
  transform: (res) => res.data.data,
  customerId: 123,
  onSelect: (formData) => {
    console.log('Selected:', formData);
  }
});
</script>

<template>
  <input
    v-model="nationalAddress.searchValue.value"
    placeholder="Search..."
  />
  
  <ul v-if="nationalAddress.items.value.length">
    <li 
      v-for="item in nationalAddress.items.value"
      :key="item.value"
      @click="nationalAddress.handleSelect(item.value)"
    >
      {{ item.label }}
    </li>
  </ul>
</template>`,
    },
    
    // ===== VUE 2 MIXINS =====
    {
      name: 'AddressSystemMixin (Vue 2)',
      category: 'vue',
      description: 'Vue 2 mixin for complete address form functionality with validation and async data',
      signature: 'AddressSystemMixin',
      example: `<template>
  <form @submit.prevent="handleSubmit">
    <!-- Using getFieldProps -->
    <select v-bind="getFieldProps('country')">
      <option v-for="c in countries" :key="c.id" :value="c.id">
        {{ c.name }}
      </option>
    </select>
    
    <!-- Using currentCities -->
    <select v-bind="getFieldProps('city')">
      <option v-for="city in currentCities" :key="city.id" :value="city.id">
        {{ city.name }}
      </option>
    </select>
    
    <button :disabled="!meta.isValid">
      {{ meta.isSubmitting ? 'Saving...' : 'Save' }}
    </button>
  </form>
</template>

<script>
import { AddressSystemMixin } from 'salla-address-manager/vue2';

export default {
  mixins: [AddressSystemMixin],
  
  created() {
    this.initializeAddressSystem({
      locale: 'en',
      validateOnChange: true
    });
  },
  
  methods: {
    async handleSubmit() {
      const submitHandler = this.handleSubmit(
        async (data) => {
          const payload = this.toAPIPayload({
            fieldMappings: {
              country: 'country_id',
              city: 'city_id'
            },
            includeNames: ['district']
          });
          
          await fetch('/api/addresses', {
            method: 'POST',
            body: JSON.stringify(payload)
          });
        }
      );
      
      await submitHandler();
    }
  }
};
</script>

// Available computed properties:
// - data: form data
// - errors: validation errors
// - fields: form fields
// - countries, cities, districts: async data
// - currentCities, currentDistricts: filtered data
// - loading: { form, countries, cities, districts }
// - meta: { isValid, isDirty, isSubmitting }
// - touched: touched fields

// Available methods:
// - setValue(field, value): set field value
// - setFieldTouched(field, touched): mark field as touched
// - handleSubmit(onValid, onInvalid): form submission
// - toAPIPayload(config): transform to API format
// - getFieldProps(field): get field props
// - validate(), validateField(), clearErrors()
// - refreshCountries(), refreshCities(), refreshDistricts()`,
    },
    {
      name: 'NationalAddressMixin (Vue 2)',
      category: 'vue',
      description: 'Vue 2 mixin for Saudi National Address autocomplete with auto-population',
      signature: 'NationalAddressMixin',
      example: `<template>
  <div>
    <input
      v-model="nationalSearchValue"
      placeholder="ابحث بالعنوان الوطني..."
    />
    
    <ul v-if="nationalItems.length">
      <li 
        v-for="item in nationalItems"
        :key="item.value"
        @click="selectNationalAddress(item.value)"
      >
        {{ item.label }}
      </li>
    </ul>
    
    <div v-if="isNationalAddressLoading">جاري البحث...</div>
  </div>
</template>

<script>
import { 
  AddressSystemMixin, 
  NationalAddressMixin 
} from 'salla-address-manager/vue2';

export default {
  mixins: [AddressSystemMixin, NationalAddressMixin],
  
  created() {
    this.initializeAddressSystem({ locale: 'ar' });
    
    this.initializeNationalAddress({
      queryFn: this.fetchAddresses,
      transform: (res) => res.data.data,
      customerId: 123,
      onSelect: (formData) => {
        console.log('Auto-populated:', formData);
      }
    });
  },
  
  methods: {
    async fetchAddresses({ address, customer_id }) {
      return await this.$axios.get('/api/national-addresses', {
        params: { address, customer_id }
      });
    }
  }
};
</script>

// Available data:
// - nationalSearchValue: search input
// - nationalItems: autocomplete results
// - isNationalAddressLoading: loading state
// - nationalAddressError: error state

// Available methods:
// - initializeNationalAddress(options): initialize
// - selectNationalAddress(value): select result
// - clearNationalAddress(): clear search
// - retryNationalAddress(): retry search`,
    },
    
    // ===== CORE API =====
    {
      name: 'createAddressClient',
      category: 'core',
      description: 'Create a professional, enterprise-grade address client for vanilla JS, React, or Vue',
      signature: 'createAddressClient(options?: AddressClientOptions): AddressClient',
      params: [
        {
          name: 'locale',
          type: "'en' | 'ar'",
          description: 'Application locale',
          optional: true,
        },
        {
          name: 'validateOnChange',
          type: 'boolean',
          description: 'Enable real-time validation',
          optional: true,
        },
        {
          name: 'validateOnBlur',
          type: 'boolean',
          description: 'Enable validation on field blur',
          optional: true,
        },
        {
          name: 'validationDelay',
          type: 'number',
          description: 'Debounce delay for validation (ms)',
          optional: true,
        },
        {
          name: 'countryOptions',
          type: 'CountryOptionsInput',
          description: 'Country options config',
          optional: true,
        },
        {
          name: 'cityOptions',
          type: 'CityOptionsInput',
          description: 'City options config',
          optional: true,
        },
        {
          name: 'defaultValues',
          type: 'AddressFormData | Promise<AddressFormData>',
          description: 'Initial form values',
          optional: true,
        },
      ],
      returns: {
        type: 'AddressClient',
        description: 'Complete client API with form management, validation, and CRUD operations',
      },
      example: `import { createAddressClient } from 'salla-address-manager';

const client = createAddressClient({
  locale: 'ar',
  validateOnChange: true,
  validationDelay: 300,
  countryOptions: {
    queryFn: () => fetch('/api/countries').then(r => r.json()),
    transform: (res) => res.data.data
  }
});

// Update form fields
client.setValue('country', '1473353380');
client.setValue('city', 'riyadh');

// Get form state
console.log(client.data); // { country: '...', city: '...', ... }
console.log(client.state); // { isValid, isDirty, errors, ... }

// Validate and submit
if (await client.validateForm()) {
  await client.submitForm();
}

// Event listeners
client.on('form:change', (state) => {
  console.log('Form changed:', state);
});

// Get countries and cities
const countries = await client.countries;
const cities = await client.getCities('1473353380');`,
    },
    {
      name: 'createAddressSystem',
      category: 'core',
      description: 'Create a lower-level address system for advanced use cases (recommended: use createAddressClient instead)',
      signature: 'createAddressSystem(config?: AddressStoreConfig): AddressSystem',
      params: [
        {
          name: 'locale',
          type: "'en' | 'ar'",
          description: 'Application locale',
          optional: true,
        },
        {
          name: 'validation',
          type: 'ValidationConfig',
          description: 'Global validation settings',
          optional: true,
        },
        {
          name: 'countryOptions',
          type: 'CountryOptionsInput',
          description: 'Country options config',
          optional: true,
        },
      ],
      returns: {
        type: 'AddressSystem',
        description: 'System instance with addressStore, countryService, and createFormController',
      },
      example: `import { createAddressSystem } from 'salla-address-manager';

const system = createAddressSystem({
  locale: 'en',
  validation: {
    validateOnChange: true,
    debounceMs: 300
  }
});

const countries = await system.getCountries();
const formController = system.createFormController();

formController.on('form:state-change', (event) => {
  console.log('Form state:', event.data);
});`,
    },
    // ===== HELPERS =====
    {
      name: 'addressToFormDefaults',
      category: 'helpers',
      description: 'Transform Salla API address to form default values - handles all common Salla field mappings',
      signature: 'addressToFormDefaults(address: SallaAddressItem, config?: DefaultValuesConfig): AddressFormData',
      params: [
        {
          name: 'address',
          type: 'SallaAddressItem',
          description: 'Salla API address object',
        },
        {
          name: 'customMappings',
          type: 'Record<string, (address: SallaAddressItem) => string>',
          description: 'Custom field mappings',
          optional: true,
        },
        {
          name: 'includeDistrictId',
          type: 'boolean',
          description: 'Use district ID instead of name (default: true)',
          optional: true,
        },
      ],
      returns: {
        type: 'AddressFormData',
        description: 'Form data ready for defaultValues',
      },
      example: `import { useAddressForm } from 'salla-address-manager/react';
import { addressToFormDefaults } from 'salla-address-manager/helpers';

// Fetch existing address from Salla API
const existingAddress = await fetch('/api/addresses/123').then(r => r.json());
// existingAddress = {
//   id: 123,
//   country: { id: 1, name: 'Saudi Arabia' },
//   city: { id: 50, name: 'Riyadh' },
//   district: { id: 100, name: 'Al Malaz' },
//   street: 'King Fahd Road',
//   building_number: '1234',
//   postal_code: '12345'
// }

const form = useAddressForm({
  locale: 'ar',
  defaultValues: addressToFormDefaults(existingAddress, {
    // Custom mappings if needed
    customMappings: {
      area: (addr) => addr.custom_field || addr.local || ''
    },
    includeDistrictId: true
  })
});

// Form is now pre-filled with existing address!`,
    },
    {
      name: 'toSallaPayload',
      category: 'helpers',
      description: 'Transform form data to Salla API payload format with proper field mappings',
      signature: 'toSallaPayload(basePayload: Record<string, unknown>, config?: SallaPayloadConfig): Record<string, unknown>',
      params: [
        {
          name: 'basePayload',
          type: 'Record<string, unknown>',
          description: 'Base payload from form.toAPIPayload()',
        },
        {
          name: 'soft',
          type: 'boolean',
          description: 'Include soft flag (default: true)',
          optional: true,
        },
        {
          name: 'type',
          type: 'string',
          description: 'Request type (default: "location_id")',
          optional: true,
        },
        {
          name: 'customTransform',
          type: '(payload: Record<string, unknown>) => Record<string, unknown>',
          description: 'Custom payload transformations',
          optional: true,
        },
      ],
      returns: {
        type: 'Record<string, unknown>',
        description: 'API-ready payload for Salla',
      },
      example: `import { useAddressForm } from 'salla-address-manager/react';
import { toSallaPayload } from 'salla-address-manager/helpers';

const form = useAddressForm({ locale: 'ar' });

const handleSubmit = form.handleSubmit(async (formData) => {
  // Step 1: Get base payload
  const basePayload = form.toAPIPayload({
    fieldMappings: {
      country: 'country_id',
      city: 'city_id',
      district: 'district_id'
    },
    includeNames: ['district'],
    defaults: { district_id: 0 }
  });

  // Step 2: Transform to Salla format
  const requestPayload = toSallaPayload(basePayload, {
    soft: true,
    type: 'location_id',
    customTransform: (payload) => ({
      ...payload,
      // Add custom fields
      lat: 24.7136,
      lng: 46.6753
    })
  });

  // Step 3: Send to API
  await fetch('/api/addresses', {
    method: 'POST',
    body: JSON.stringify(requestPayload)
  });
});`,
    },
    {
      name: 'createCountryOptions',
      category: 'helpers',
      description: 'Helper to create country options configuration',
      signature: 'createCountryOptions(input: CountryOptionsInput): CountryOptionsInput',
      params: [
        {
          name: 'input',
          type: 'Country[] | { queryFn: () => Promise<unknown>; transform?: (res: unknown) => Country[] }',
          description: 'Static array or async config',
        },
      ],
      returns: {
        type: 'CountryOptionsInput',
        description: 'Country options config',
      },
      example: `import { useAddressForm } from 'salla-address-manager/react';
import { createCountryOptions } from 'salla-address-manager/helpers';

const form = useAddressForm({
  locale: 'ar',
  countryOptions: createCountryOptions({
    queryFn: () => fetch('/api/countries').then(r => r.json()),
    transform: (response) => {
      // Transform Salla API response
      return response.data.data.map(country => ({
        id: country.id,
        name: country.name // Already localized by API
      }));
    }
  })
});`,
    },
    {
      name: 'toAPIPayload',
      category: 'helpers',
      description: 'Transform form data to API payload format (part of form instance - use form.toAPIPayload())',
      signature: 'form.toAPIPayload(config?: PayloadTransformConfig): Record<string, unknown>',
      params: [
        {
          name: 'fieldMappings',
          type: 'Record<string, string>',
          description: 'Map form fields to API fields (e.g., { country: "country_id" })',
          optional: true,
        },
        {
          name: 'includeNames',
          type: 'string[]',
          description: 'Fields to include both ID and name (e.g., ["district"])',
          optional: true,
        },
        {
          name: 'defaults',
          type: 'Record<string, unknown>',
          description: 'Default values to merge into payload',
          optional: true,
        },
        {
          name: 'exclude',
          type: 'string[]',
          description: 'Fields to exclude from payload',
          optional: true,
        },
        {
          name: 'transformers',
          type: 'Record<string, (value: unknown) => unknown>',
          description: 'Custom field transformations',
          optional: true,
        },
      ],
      returns: {
        type: 'Record<string, unknown>',
        description: 'Transformed API payload',
      },
      example: `import { useAddressForm } from 'salla-address-manager/react';

const form = useAddressForm({ locale: 'ar' });

// Basic transformation
const payload1 = form.toAPIPayload({
  fieldMappings: {
    country: 'country_id',
    city: 'city_id',
    district: 'district_id'
  }
});

// Include names for certain fields
const payload2 = form.toAPIPayload({
  fieldMappings: {
    country: 'country_id',
    city: 'city_id'
  },
  includeNames: ['district'], // Includes district_id AND district_name
  defaults: {
    lat: 24.7136,
    lng: 46.6753,
    type: 'location_id'
  },
  transformers: {
    block: (value) => String(value).toUpperCase()
  }
});

// Result:
// {
//   country_id: '1473353380',
//   city_id: 'riyadh',
//   district_id: '100',
//   district_name: 'Al Malaz', // Auto-fetched name
//   street: 'King Fahd Road',
//   block: 'A12', // Transformed
//   lat: 24.7136, // From defaults
//   lng: 46.6753,
//   type: 'location_id'
// }`,
    },
    {
      name: 'extractSallaData',
      category: 'helpers',
      description: 'Extract nested data from Salla API response (handles { data: { data: [...] } } pattern)',
      signature: 'extractSallaData<T>(response: unknown): T[]',
      params: [
        {
          name: 'response',
          type: 'unknown',
          description: 'Salla API response',
        },
      ],
      returns: {
        type: 'T[]',
        description: 'Extracted array',
      },
      example: `import { extractSallaData } from 'salla-address-manager/helpers';

const response = await fetch('/api/countries').then(r => r.json());
// response = { data: { data: [{ id: 1, name: 'Saudi Arabia' }, ...] } }

const countries = extractSallaData<{ id: number; name: string }>(response);
// countries = [{ id: 1, name: 'Saudi Arabia' }, ...]`,
    },
    // ===== TYPESCRIPT TYPES =====
    {
      name: 'AddressFormData',
      category: 'types',
      description: 'TypeScript type for address form data with index signature for country-specific fields',
      signature: 'type AddressFormData = { country: string; city: string; district: string; street: string; description: string; [key: string]: string }',
      example: `import type { AddressFormData } from 'salla-address-manager';

const address: AddressFormData = {
  country: '1473353380', // Saudi Arabia
  city: 'riyadh',
  district: 'Al Malaz',
  street: '123 King Fahd Road',
  description: 'Near the mall',
  // Country-specific fields (Saudi Arabia)
  building_number: '1234',
  postal_code: '12345',
  additional_number: '5678',
  // Other possible fields
  area: 'Al Malaz',
  block: 'A',
  governorate: 'Riyadh'
};`,
      returns: {
        type: 'type',
        description: 'TypeScript type definition for form data',
      },
    },
    {
      name: 'AddressClientOptions',
      category: 'types',
      description: 'Configuration options for createAddressClient and useAddressForm',
      signature: 'interface AddressClientOptions { locale?: "en" | "ar"; validateOnChange?: boolean; ... }',
      example: `import type { AddressClientOptions } from 'salla-address-manager';
import { createAddressClient } from 'salla-address-manager';

const options: AddressClientOptions = {
  locale: 'ar',
  validateOnChange: true,
  validateOnBlur: true,
  validationDelay: 300,
  countryOptions: {
    queryFn: () => fetch('/api/countries').then(r => r.json()),
    transform: (res) => res.data.data
  },
  cityOptions: {
    queryFn: (countryId) => fetch(\`/api/countries/\${countryId}/cities\`).then(r => r.json()),
    transform: (res) => res.data.data
  },
  defaultValues: {
    country: '1473353380',
    city: 'riyadh'
  },
  enableNationalAddress: true,
  requiredFields: {
    postal_code: true,
    description: false,
    building_number: true
  }
};

const client = createAddressClient(options);`,
      returns: {
        type: 'type',
        description: 'Configuration interface',
      },
    },
    {
      name: 'UseAddressFormReturn',
      category: 'types',
      description: 'Return type of useAddressForm hook with all form state and methods',
      signature: 'interface UseAddressFormReturn { data: AddressFormData; state: FormState; countries: Country[]; ... }',
      example: `import type { UseAddressFormReturn } from 'salla-address-manager/react';
import { useAddressForm } from 'salla-address-manager/react';

function MyForm() {
  const form: UseAddressFormReturn = useAddressForm({ locale: 'en' });
  
  // Access form state
  form.data; // { country: '', city: '', ... }
  form.state; // { isValid, isDirty, isSubmitting, errors }
  form.fields; // [{ key: 'country', label: 'Country', ... }]
  form.errors; // { street: 'Street is required' }
  
  // Access async data
  form.countries; // [{ id: '1', name: 'Saudi Arabia' }, ...]
  form.currentCities; // Cities for selected country
  form.currentDistricts; // Districts for selected city
  form.loading; // { form, countries, cities, districts, any }
  
  // Methods
  form.setValue('country', '1473353380');
  form.setValues({ country: '...', city: '...' });
  form.validateForm();
  form.handleSubmit(onValid, onInvalid);
  form.getFieldProps('country');
  form.toAPIPayload({ fieldMappings: { ... } });
  form.getCitiesForCountry('1473353380');
  form.getDistrictsForCity('riyadh');
}`,
      returns: {
        type: 'type',
        description: 'Complete form API interface',
      },
    },
    {
      name: 'Country',
      category: 'types',
      description: 'TypeScript type for country object',
      signature: 'interface Country { id: string | number; name: string; code?: string }',
      example: `import type { Country } from 'salla-address-manager';

const country: Country = {
  id: '1473353380',
  name: 'Saudi Arabia',
  code: 'SA'
};`,
      returns: {
        type: 'type',
        description: 'Country interface',
      },
    },
    {
      name: 'City',
      category: 'types',
      description: 'TypeScript type for city object',
      signature: 'interface City { id: string | number; name: string }',
      example: `import type { City } from 'salla-address-manager';

const city: City = {
  id: 'riyadh',
  name: 'Riyadh'
};`,
      returns: {
        type: 'type',
        description: 'City interface',
      },
    },
    {
      name: 'FormState',
      category: 'types',
      description: 'TypeScript type for form state',
      signature: 'interface FormState { isValid: boolean; isDirty: boolean; isSubmitting: boolean; errors: Record<string, string> }',
      example: `import type { FormState } from 'salla-address-manager';

const state: FormState = {
  isValid: false,
  isDirty: true,
  isSubmitting: false,
  isFetching: true,
  isFetchingCountries: false,
  isFetchingCities: true,
  isFetchingDistricts: false,
  errors: {
    street: 'Street is required',
    postal_code: 'Postal code must be 5 digits'
  }
};`,
      returns: {
        type: 'type',
        description: 'Form state interface',
      },
    },
  ];

  const categories = [
    { id: 'all', name: 'All' },
    { id: 'react', name: 'React Hooks' },
    { id: 'vue', name: 'Vue Composables' },
    { id: 'core', name: 'Core API' },
    { id: 'helpers', name: 'Helpers' },
    { id: 'types', name: 'TypeScript Types' },
  ];

  const filteredItems = apiItems.filter((item) => {
    const matchesSearch =
      searchQuery === '' ||
      item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.description.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesCategory =
      selectedCategory === 'all' || item.category === selectedCategory;

    return matchesSearch && matchesCategory;
  });

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="container-custom py-12">
          <h1 className="text-4xl font-bold mb-4">API Reference</h1>
          <p className="text-xl text-gray-600 max-w-3xl">
            Complete API reference for all hooks, methods, and types in Salla
            Address Manager.
          </p>
        </div>
      </div>

      <div className="container-custom section">
        {/* Search and Filters */}
        <div className="card mb-8">
          <div className="grid md:grid-cols-2 gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search API..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-salla-primary focus:border-transparent"
              />
            </div>

            {/* Category Filter */}
            <div className="flex gap-2 overflow-x-auto">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                    selectedCategory === category.id
                      ? 'bg-salla-primary text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {category.name}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* API Items */}
        <div className="space-y-6">
          {filteredItems.length > 0 ? (
            filteredItems.map((item) => (
              <div
                key={item.name}
                id={item.name}
                className="card scroll-mt-24"
              >
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h2 className="text-2xl font-bold font-mono text-salla-primary mb-2">
                      {item.name}
                    </h2>
                    <p className="text-gray-600">{item.description}</p>
                  </div>
                  <span
                    className={`px-3 py-1 rounded text-sm font-medium ${
                      item.category === 'react'
                        ? 'bg-blue-100 text-blue-800'
                        : item.category === 'vue'
                        ? 'bg-green-100 text-green-800'
                        : item.category === 'core'
                        ? 'bg-purple-100 text-purple-800'
                        : item.category === 'helpers'
                        ? 'bg-orange-100 text-orange-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {item.category}
                  </span>
                </div>

                {/* Signature */}
                <div className="mb-6">
                  <h3 className="font-bold text-sm text-gray-700 mb-2">
                    SIGNATURE
                  </h3>
                  <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto">
                    <code className="text-sm">{item.signature}</code>
                  </pre>
                </div>

                {/* Parameters */}
                {item.params && item.params.length > 0 && (
                  <div className="mb-6">
                    <h3 className="font-bold text-sm text-gray-700 mb-3">
                      PARAMETERS
                    </h3>
                    <div className="space-y-3">
                      {item.params.map((param) => (
                        <div
                          key={param.name}
                          className="border-l-2 border-salla-primary pl-4"
                        >
                          <div className="flex items-center space-x-2 mb-1">
                            <code className="font-mono font-bold text-sm">
                              {param.name}
                            </code>
                            <span className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded">
                              {param.type}
                            </span>
                            {param.optional && (
                              <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                                optional
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-600">
                            {param.description}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Returns */}
                {item.returns && (
                  <div className="mb-6">
                    <h3 className="font-bold text-sm text-gray-700 mb-3">
                      RETURNS
                    </h3>
                    <div className="border-l-2 border-green-500 pl-4">
                      <code className="font-mono font-bold text-sm block mb-1">
                        {item.returns.type}
                      </code>
                      <p className="text-sm text-gray-600">
                        {item.returns.description}
                      </p>
                    </div>
                  </div>
                )}

                {/* Example */}
                <div>
                  <h3 className="font-bold text-sm text-gray-700 mb-2">
                    EXAMPLE
                  </h3>
                  <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto">
                    <code className="text-sm">{item.example}</code>
                  </pre>
                </div>
              </div>
            ))
          ) : (
            <div className="card text-center py-12">
              <Book className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-600 mb-2">
                No Results Found
              </h3>
              <p className="text-gray-500">
                Try adjusting your search or filter criteria.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ApiReferencePage;

